import React, { memo, ReactElement, useCallback, useMemo } from 'react';
import { Box, TextField, Slider, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useIntl } from 'react-intl';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';

import { NumericChecklistAnswerModel } from 'shared/domain/template/inspectionTemplateModel';
import {
  answerLabelSx,
  inputContainerSx,
  numericInputColumnSx,
  numericInputMaxThirdSx,
  numericRowWithSliderSx,
  numericSliderSx,
  numericUnitColumnSx,
  sliderContainerSx,
  unitLabelSx,
  warningTextSx,
  numericInputNormWarningSx,
  requiredIndicatorSx,
  getAnswerFieldCardSx,
  answerFieldBodySx,
  disabledOutlinedInputBorderSx,
} from './styles';

interface NumericAnswerInputProps {
  answerConfig: NumericChecklistAnswerModel;
  value?: number;
  onChange: (value: number | undefined) => void;
  onBlur?: () => void;
  onCommit?: (value: number) => void;
  disabled?: boolean;
  hideLabel?: boolean;
}

function NumericAnswerInputComponent({
  answerConfig,
  value,
  onChange,
  onBlur,
  onCommit,
  disabled,
  hideLabel = false,
}: NumericAnswerInputProps): ReactElement {
  const intl = useIntl();

  const hasSlider = useMemo(() => {
    return (
      answerConfig.step !== undefined &&
      answerConfig.rangeMin !== undefined &&
      answerConfig.rangeMax !== undefined
    );
  }, [answerConfig]);

  const isOutOfNorm = useMemo(() => {
    if (value === undefined) return false;
    if (answerConfig.normMin !== undefined && value < answerConfig.normMin) {
      return true;
    }
    if (answerConfig.normMax !== undefined && value > answerConfig.normMax) {
      return true;
    }
    return false;
  }, [value, answerConfig.normMin, answerConfig.normMax]);

  const normRangeText = useMemo(() => {
    if (answerConfig.normMin !== undefined && answerConfig.normMax !== undefined) {
      return `${answerConfig.normMin} - ${answerConfig.normMax}`;
    }
    if (answerConfig.normMin !== undefined) {
      return `≥ ${answerConfig.normMin}`;
    }
    if (answerConfig.normMax !== undefined) {
      return `≤ ${answerConfig.normMax}`;
    }
    return null;
  }, [answerConfig.normMin, answerConfig.normMax]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const val = event.target.value;
      if (val === '') {
        onChange(undefined);
      } else {
        const numVal = parseFloat(val);
        if (!isNaN(numVal)) {
          onChange(numVal);
        }
      }
    },
    [onChange]
  );

  const handleSliderChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      if (typeof newValue === 'number') {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const handleSliderCommit = useCallback(
    (_: Event | React.SyntheticEvent, newValue: number | number[]) => {
      if (typeof newValue === 'number') {
        onCommit?.(newValue);
      }
    },
    [onCommit]
  );

  const fieldSx = useMemo(
    () =>
      [
        isOutOfNorm ? numericInputNormWarningSx : null,
        disabled ? disabledOutlinedInputBorderSx : null,
      ] as SxProps<Theme>,
    [disabled, isOutOfNorm]
  );

  return (
    <Box sx={hideLabel ? answerFieldBodySx : getAnswerFieldCardSx(answerConfig.type)}>
      {hasSlider ? (
        <>
          {answerConfig.label && !hideLabel ? (
            <Typography component='p' sx={answerLabelSx}>
              {answerConfig.label}
              {answerConfig.isRequired && (
                <Box component='span' sx={requiredIndicatorSx}>*</Box>
              )}
            </Typography>
          ) : null}
          <Box sx={numericRowWithSliderSx}>
            <Box sx={numericInputColumnSx}>
              <TextField
                type='number'
                size='medium'
                fullWidth
                value={value ?? ''}
                onChange={handleInputChange}
                onBlur={onBlur}
                disabled={disabled}
                sx={fieldSx}
                inputProps={{
                  step: answerConfig.step,
                  min: answerConfig.rangeMin,
                  max: answerConfig.rangeMax,
                }}
              />
            </Box>
            <Box sx={numericUnitColumnSx}>
              {answerConfig.unit ? (
                <Typography component='span' sx={unitLabelSx}>
                  {answerConfig.unit}
                </Typography>
              ) : null}
            </Box>
            <Box sx={sliderContainerSx}>
              <Slider
                sx={numericSliderSx}
                value={value ?? answerConfig.rangeMin ?? 0}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderCommit}
                min={answerConfig.rangeMin}
                max={answerConfig.rangeMax}
                step={answerConfig.step}
                disabled={disabled}
                valueLabelDisplay={value !== undefined ? 'auto' : 'off'}
              />
            </Box>
          </Box>
        </>
      ) : (
        <>
          {answerConfig.label && !hideLabel ? (
            <Typography component='p' sx={answerLabelSx}>
              {answerConfig.label}
              {answerConfig.isRequired && (
                <Box component='span' sx={requiredIndicatorSx}>*</Box>
              )}
            </Typography>
          ) : null}
          <Box sx={inputContainerSx}>
            <Box sx={numericInputMaxThirdSx}>
              <TextField
                type='number'
                size='medium'
                fullWidth
                value={value ?? ''}
                onChange={handleInputChange}
                onBlur={onBlur}
                disabled={disabled}
                sx={fieldSx}
                inputProps={{
                  step: answerConfig.step,
                  min: answerConfig.rangeMin,
                  max: answerConfig.rangeMax,
                }}
              />
            </Box>
            {answerConfig.unit ? (
              <Typography component='span' sx={unitLabelSx}>
                {answerConfig.unit}
              </Typography>
            ) : null}
          </Box>
        </>
      )}

      {isOutOfNorm && normRangeText && (
        <Box sx={warningTextSx} role='status'>
          <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          <span>
            {formatTranslation(
              intl,
              InspectionTranslationKeys.numericAnswerOutOfNormWarning,
              { range: normRangeText }
            )}
          </span>
        </Box>
      )}
    </Box>
  );
}

export const NumericAnswerInput = memo(NumericAnswerInputComponent);
